import { FormComponent } from './form-component';
import {
  Observable,
  Subscription,
  BehaviorSubject
} from 'rxjs';
import {
  FormGroupState,
  Unboxed
} from 'ngrx-forms';
import {
  Inject,
  OnDestroy,
  Input,
  Optional,
  Injector
} from '@angular/core';
import { FORM_CONTAINER_CONTEXT } from '../tokens';
import {
  Store,
  MemoizedSelector
} from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { FormsSources } from './forms-sources';
import { AbstractFormDefinition } from './abstract-form-definition';
import { DxFormGroup } from './form-group';
import { DX_FORM_ID } from './tokens';

export interface OnSetFormDefinition {
  dxOnSetFormDefinition(): void;
}

export const dxOnSetFormDefinition = 'dxOnSetFormDefinition';

export class AbstractFormComponent2<
  FormValue,
  FormGroup extends DxFormGroup<FormValue, any, any> = DxFormGroup<
    FormValue,
    null,
    null
  >,
  FormDefinition extends AbstractFormDefinition<
    FormValue,
    FormGroup
  > = AbstractFormDefinition<FormValue, FormGroup>
> implements FormComponent<FormValue>, OnDestroy {

  public get formDefinition(): FormDefinition {
    return this._formDefinition;
  }

  @Input() public set formDefinition(value: FormDefinition) {
    if (this._formDefinition) {
      throw new Error(`[AbstractFormComponent2::(set)formDefinition::${this._formDefinition.formId}] form definition is already defined`);
    }
    this._formDefinition = value;
    if (this._formDefinition) {
      this._formDefinition.ngOnInit();
      if (this[ dxOnSetFormDefinition ] && typeof this[ dxOnSetFormDefinition ] === 'function') {
        this[ dxOnSetFormDefinition ]();
      }
      this.isReady$.next(true);
    } else {
      this.logger.warn('[AbstractFormComponent2::(set)formDefinition] form definition is not defined');
    }
  }

  private _formDefinition: FormDefinition | null = null;

  public get formId(): string {
    return this.formDefinition && this.formDefinition.formId;
  }

  public get collectionPath(): string {
    return this.formDefinition && this.formDefinition.collectionPath;
  }

  public get formState$(): Observable<FormGroupState<FormValue>> {
    return this.formDefinition && this.formDefinition.formState$;
  }

  public get group(): FormGroup {
    return this.formDefinition && this.formDefinition.group as FormGroup;
  }

  public get value(): Unboxed<FormValue> {
    return this.group && this.group.__value;
  }

  public get value$(): Observable<Unboxed<FormValue>> {
    return this.group && this.group.__value$;
  }

  /**
   * @deprecated
   * @param value
   */
  public set formState$(value: Observable<FormGroupState<FormValue>>) {
    throw new Error(
      `[AbstractFormComponent2::${this.formId}] set formState$ is deprecated`
    );
  }

  protected _subscriptions = new Subscription();

  public isReady$ = new BehaviorSubject(false);

  constructor(
    @Optional() @Inject(FORM_CONTAINER_CONTEXT) context: any,
    @Optional() @Inject(DX_FORM_ID) formId,
    public readonly store$: Store<any>,
    public readonly logger: NGXLogger,
    public readonly formsSources: FormsSources,
    public readonly injector: Injector
  ) {
    if (context || formId) {
      if (!formId && !context && !context.formId) {
        throw new Error('Form id is not set');
      }
      this.formsSources.getForm$<FormDefinition>(formId || context.formId).subscribe(fd => this.formDefinition = fd);
    } else {
      this.logger.warn(
        '[AbstractFormComponent::constructor] FORM_CONTAINER_CONTEXT not set'
      );
    }
  }

  public select<T>(selector: MemoizedSelector<any, T>): Observable<T> {
    return this.store$.select(selector);
  }

  public trackBy(index, item) {
    return item.id;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this.formDefinition.ngOnDestroy();
  }

  public getValue<V>(controlPath: string): V {
    return this.formDefinition.getValue(controlPath);
  }

  public getValue$<V>(controlPath: string): Observable<V> {
    return this.formDefinition.getValue$(controlPath);
  }
}
